import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DocumentAuditEvent,
  getDocumentAuditEventsFromAPI,
  isDocumentAuditAccessPersistenceUnavailableError,
  isDocumentAuditAccessStateChangedError,
} from '../services/eCMRService';

const safePublicCount = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;

const auditEventDetailEntries = (event: DocumentAuditEvent): Array<[string, number]> => {
  const detailCount = safePublicCount(event.details?.detailCount);
  return detailCount !== undefined ? [['detailCount', detailCount]] : [];
};

const formatTimestamp = (event: DocumentAuditEvent): string =>
  event.eventTimestamp || event.timestamp || 'sin timestamp';

const auditEventVersionReferenceLabel = (event: DocumentAuditEvent): string =>
  event.documentVersionReferencePresent || event.documentVersionId || event.documentVersionNumber !== undefined
    ? 'Referencia disponible'
    : '-';

const auditEventActorLabel = (event: DocumentAuditEvent): string =>
  event.actorReferencePresent || event.actorUserId ? 'actor registrado' : 'actor no identificado';

const DOCUMENT_AUDIT_LOAD_ERROR = 'No se pudo cargar la trazabilidad de auditoria. Revisa permisos, estado del documento o disponibilidad del backend.';
const DOCUMENT_AUDIT_ACCESS_CONFLICT_ERROR =
  'El documento cambio mientras se cargaba la auditoria. Recarga la version vigente antes de continuar.';
const DOCUMENT_AUDIT_ACCESS_UNAVAILABLE_ERROR =
  'La auditoria esta temporalmente bloqueada porque no se pudo registrar su evidencia de acceso.';

const documentAuditAccessErrorMessage = (error: unknown): string => {
  if (isDocumentAuditAccessStateChangedError(error)) {
    return DOCUMENT_AUDIT_ACCESS_CONFLICT_ERROR;
  }
  if (isDocumentAuditAccessPersistenceUnavailableError(error)) {
    return DOCUMENT_AUDIT_ACCESS_UNAVAILABLE_ERROR;
  }
  return DOCUMENT_AUDIT_LOAD_ERROR;
};

const DocumentAuditTrail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [events, setEvents] = useState<DocumentAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!documentId) {
      setError('Missing API document id.');
      setLoading(false);
      return;
    }

    const loadAuditEvents = async () => {
      setLoading(true);
      setError('');
      try {
        setEvents(await getDocumentAuditEventsFromAPI(documentId));
      } catch (loadError) {
        setEvents([]);
        setError(documentAuditAccessErrorMessage(loadError));
      } finally {
        setLoading(false);
      }
    };

    loadAuditEvents();
  }, [documentId]);

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Trazabilidad de auditoria</h1>
          <p className="text-sm text-gray-600">Eventos backend sanitizados para documento API {documentId || 'desconocido'}.</p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Volver a documentos</Link>
      </div>

      <div className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-blue-800">
        Baseline tecnico parcial: esta vista expone eventos de auditoria sanitizados del sistema de registro. No sustituye una retencion WORM completa ni una revision legal.
      </div>

      {loading && <p>Cargando eventos de auditoria...</p>}
      {error && <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>}

      {!loading && !error && events.length === 0 && (
        <p className="rounded border-l-4 border-yellow-500 bg-yellow-100 p-4 text-yellow-800">No hay eventos de auditoria disponibles para este documento.</p>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="overflow-x-auto rounded bg-white shadow-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Evento</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Version</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actor / roles</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Detalles sanitizados</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {events.map((event, index) => {
                const detailEntries = auditEventDetailEntries(event);
                return (
                  <tr key={event.eventId || `${event.eventType}-${index}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatTimestamp(event)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{event.eventType}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{auditEventVersionReferenceLabel(event)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{auditEventActorLabel(event)}</div>
                      <div className="text-xs text-gray-500">{event.actorRoles?.join(', ') || 'sin roles'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {detailEntries.length > 0 ? (
                        <dl className="grid gap-1">
                          {detailEntries.map(([key, value]) => (
                            <div key={key}>
                              <dt className="inline font-medium">{key}: </dt>
                              <dd className="inline">{value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        'Sin detalles adicionales'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentAuditTrail;
